/**
 * EffortBuilder — Iter 241
 * Bottom-up effort breakdown for an Estimate line item.
 *
 * Renders inside an expanded row of the ExcelEstimateLines grid. Users
 * add rows of (Designation × Hours × Rate), and the parent line total
 * is automatically rolled up when effort_mode = "auto".
 *
 * RBAC:
 *   - ratesHidden   → hides rate / cost / total columns; effort-only role
 *                      can still add designation × hours breakdowns.
 *   - !ratesHidden  → full financial editing.
 */
import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import SearchableSelect from "@/components/ui/SearchableSelect";

function fmt(n, currency = "INR") {
  const val = Number(n || 0);
  return `${currency === "INR" ? "₹" : currency + " "}${val.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function isRateOutOfBand(row, designations) {
  if (!row.designation_id) return false;
  const d = designations.find((x) => x.id === row.designation_id);
  if (!d) return false;
  const rate = Number(row.hourly_rate || 0);
  if (rate <= 0) return false;
  const min = d.band_min != null ? Number(d.band_min) : null;
  const max = d.band_max != null ? Number(d.band_max) : null;
  if (min != null && rate < min) return true;
  if (max != null && rate > max) return true;
  return false;
}

export default function EffortBuilder({
  line, designations, currency = "INR", members = [], wsId, estimateId,
  ratesHidden = false, effortMode = "manual",
  onSaved, onEffortModeChange, onManageDesignations,
}) {
  const buildRows = (subs) => (subs || []).map((s) => ({
    id: s.id, name: s.name || s.designation || "",
    designation_id: s.designation_id || "",
    designation: s.designation || "",
    hours: s.hours ?? 0,
    hourly_rate: s.hourly_rate ?? 0,
    // R138ap · Cost-source picker for internal cost / margin math.
    cost_source: s.cost_source || (s.designation_id ? "designation" : (s.assigned_user_id ? "user" : "designation")),
    assigned_user_id: s.assigned_user_id || "",
    custom_internal_rate: s.custom_internal_rate ?? "",
    note: s.note || "",
    _dirty: false,
  }));
  const [rows, setRows] = useState(buildRows(line.sub_items));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(buildRows(line.sub_items));
    setDirty(false);
    // eslint-disable-next-line
  }, [line.id, line.updated_at, (line.sub_items || []).length]);

  const designationOpts = useMemo(() => [
    { value: "", label: "— Free type —" },
    ...designations.map((d) => ({
      value: d.id,
      label: d.name + (d.level ? ` · ${d.level}` : ""),
      hint: d.default_hourly_rate != null
        ? `${currency === "INR" ? "₹" : currency + " "}${Number(d.default_hourly_rate).toLocaleString("en-IN")}/hr${d.band_min || d.band_max ? ` · band ${d.band_min ?? "—"}-${d.band_max ?? "—"}` : ""}`
        : undefined,
    })),
  ], [designations, currency]);

  const setCell = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [key]: val, _dirty: true } : r));
    setDirty(true);
  };
  // R138ar · Cost Source Auto-Suggest — when the manager picks an
  // assigned user, if no explicit cost source is set (or it's still on
  // the "designation" default), flip to "user" so the salary-based cost
  // math kicks in. Also fetches that user's hourly cost hint.
  const assignUser = (i, uid) => {
    setRows((prev) => prev.map((r, idx) => {
      if (idx !== i) return r;
      const auto = !r.cost_source || r.cost_source === "designation";
      return {
        ...r,
        assigned_user_id: uid,
        cost_source: auto ? "user" : r.cost_source,
        _dirty: true,
      };
    }));
    setDirty(true);
    // Fetch the salary hint (best-effort; ignored on 404)
    if (uid && wsId) {
      api.get(`/workspaces/${wsId}/employees/hourly-cost?user_id=${uid}`)
         .then(({ data }) => {
           if (data?.hourly_cost) {
             toast.success(`${data.name || "User"}'s cost basis: ${currency === "INR" ? "₹" : currency + " "}${Number(data.hourly_cost).toLocaleString("en-IN")}/hr (from ₹${data.salary_amount}/mo salary)`);
           } else {
             toast.warning(`No salary set for this user — margin will use ₹0 cost. Set it in HR → Employees.`);
           }
         })
         .catch(() => { /* silent */ });
    }
  };
  const applyDesignation = (i, did) => {
    const d = designations.find((x) => x.id === did);
    if (!d) { setCell(i, "designation_id", ""); return; }
    setRows((prev) => prev.map((r, idx) => idx === i ? {
      ...r,
      designation_id: d.id,
      designation: d.name,
      hourly_rate: !ratesHidden && (!r.hourly_rate || r.hourly_rate === 0)
        ? (d.default_hourly_rate ?? 0) : r.hourly_rate,
      name: r.name || d.name,
      _dirty: true,
    } : r));
    setDirty(true);
  };
  const addRow = () => {
    setRows((prev) => [...prev, {
      id: null, name: "", designation_id: "", designation: "",
      hours: 1, hourly_rate: 0, note: "", _dirty: true,
    }]);
    setDirty(true);
  };
  const removeRow = (i) => {
    setRows((prev) => prev.map((r, idx) => idx === i
      ? (r.id ? { ...r, _delete: true, _dirty: true } : null)
      : r).filter(Boolean));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        rows: rows.filter((r) => r.name || r.designation || r.designation_id || r.id).map((r, idx) => ({
          id: r.id || undefined,
          name: r.name || r.designation || "Effort",
          designation: r.designation || undefined,
          designation_id: r.designation_id || undefined,
          hours: Number(r.hours) || 0,
          hourly_rate: ratesHidden ? undefined : (Number(r.hourly_rate) || 0),
          // R138ap · Cost source + user/custom rate flow-through
          cost_source: r.cost_source || undefined,
          assigned_user_id: r.assigned_user_id || undefined,
          custom_internal_rate: (r.custom_internal_rate === "" || r.custom_internal_rate == null)
            ? undefined
            : Number(r.custom_internal_rate),
          note: r.note || undefined,
          position: idx,
          delete: !!r._delete,
        })),
      };
      const { data } = await api.post(
        `/estimate-line-items/${line.id}/sub-items/bulk`, payload);
      toast.success(`Effort saved · ${data.created} added, ${data.updated} updated, ${data.deleted} removed`);
      setDirty(false);
      if (onSaved) onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  // R138as · Save current effort rows as a reusable workspace template.
  const saveAsTemplate = async () => {
    const validRows = rows.filter((r) => !r._delete && (r.designation || r.designation_id || r.name));
    if (validRows.length === 0) return toast.error("Add at least one row first");
    const name = window.prompt("Template name (e.g. \"Explainer Video\", \"Product Shoot\")");
    if (!name || !name.trim()) return;
    try {
      await api.post(`/workspaces/${wsId}/effort-templates`, {
        name: name.trim(),
        description: null,
        rows: validRows.map((r) => ({
          name: r.name || r.designation || undefined,
          designation: r.designation || undefined,
          designation_id: r.designation_id || undefined,
          assigned_user_id: r.assigned_user_id || undefined,
          hours: Number(r.hours) || 0,
          hourly_rate: Number(r.hourly_rate) || 0,
          cost_source: r.cost_source || undefined,
          custom_internal_rate: (r.custom_internal_rate === "" || r.custom_internal_rate == null)
            ? undefined : Number(r.custom_internal_rate),
          note: r.note || undefined,
        })),
      });
      toast.success(`Template "${name}" saved`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to save template")); }
  };

  const visibleRows = rows.filter((r) => !r._delete);
  const totalHours = visibleRows.reduce((s, r) => s + (Number(r.hours) || 0), 0);
  const totalAmount = visibleRows.reduce(
    (s, r) => s + (Number(r.hours) || 0) * (Number(r.hourly_rate) || 0), 0);

  return (
    <div className="border-t border-border bg-muted/10 p-2 sm:p-3 space-y-3 overflow-x-auto" data-testid={`effort-builder-${line.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2 min-w-[720px] sm:min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Bottom-up Effort Builder
          </span>
          <div className="inline-flex border border-border rounded-none overflow-hidden" data-testid={`effort-mode-toggle-${line.id}`}>
            <button type="button"
              onClick={() => onEffortModeChange("manual")}
              className={`px-2 py-1 text-[10px] uppercase tracking-wide ${effortMode !== "auto" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
              data-testid={`effort-mode-manual-${line.id}`}>
              Manual
            </button>
            <button type="button"
              onClick={() => onEffortModeChange("auto")}
              className={`px-2 py-1 text-[10px] uppercase tracking-wide ${effortMode === "auto" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
              data-testid={`effort-mode-auto-${line.id}`}>
              Auto rollup
            </button>
          </div>
          {effortMode === "auto" && (
            <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5">
              Parent Rate/Qty is derived from efforts below
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onManageDesignations && (
            <button type="button" onClick={onManageDesignations}
                     className="text-[10px] px-2 py-1 border border-border hover:bg-muted text-muted-foreground"
                     data-testid={`effort-manage-designations-${line.id}`}>
              Manage designations
            </button>
          )}
          {/* R138as · Effort Templates */}
          <TemplatePicker wsId={wsId} lineId={line.id} onApplied={onSaved} />
          <button type="button" onClick={saveAsTemplate}
                   className="text-[10px] px-2 py-1 border border-border hover:bg-muted text-muted-foreground"
                   title="Save these effort rows as a reusable template"
                   data-testid={`effort-save-template-${line.id}`}>
            Save as template
          </button>
          <button type="button" onClick={addRow}
                   className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
                   data-testid={`effort-add-row-${line.id}`}>
            <Plus className="w-3 h-3" /> Add effort
          </button>
          <button type="button" onClick={save} disabled={!dirty || saving}
                   className="text-[10px] px-2 py-1 bg-primary text-primary-foreground disabled:opacity-50 font-medium flex items-center gap-1"
                   data-testid={`effort-save-${line.id}`}>
            <Save className="w-3 h-3" /> {saving ? "Saving…" : "Save efforts"}
          </button>
        </div>
      </div>

      <div className="border border-border min-w-[720px] sm:min-w-0">
        <div className="grid grid-cols-12 gap-2 px-2 py-1.5 text-[10px] uppercase text-muted-foreground bg-muted/40 border-b border-border">
          <div className="col-span-4">Designation</div>
          <div className="col-span-3">Description</div>
          <div className="col-span-1 text-right">Hrs</div>
          {!ratesHidden && <div className="col-span-2 text-right">Rate/hr</div>}
          {!ratesHidden && <div className="col-span-1 text-right">Amount</div>}
          <div className="col-span-1"></div>
        </div>
        {visibleRows.length === 0 && (
          <div className="px-4 py-4 text-center text-[11px] text-muted-foreground">
            No effort rows yet. Click <b>Add effort</b> to break down this line.
          </div>
        )}
        {rows.map((r, i) => r._delete ? null : (
          <div key={r.id || `n-${i}`}
                className={`grid grid-cols-12 gap-2 px-2 py-1.5 text-xs items-start border-b border-border last:border-0 ${r._dirty ? "bg-amber-50/40" : ""}`}
                data-testid={`effort-row-${line.id}-${i}`}>
            <div className="col-span-4 flex items-center gap-1">
              <div className="flex-1 min-w-0">
                <SearchableSelect
                  value={r.designation_id || ""}
                  onValueChange={(v) => v ? applyDesignation(i, v) : setCell(i, "designation_id", "")}
                  options={designationOpts}
                  placeholder="Pick designation"
                  testid={`effort-designation-${line.id}-${i}`}
                />
              </div>
              {!r.designation_id && (
                <input value={r.designation || ""}
                        onChange={(e) => setCell(i, "designation", e.target.value)}
                        placeholder="Free type…"
                        className="w-20 text-[11px] px-1 py-1 bg-background border border-border"
                        data-testid={`effort-designation-free-${line.id}-${i}`} />
              )}
            </div>
            <div className="col-span-3">
              <input value={r.name}
                      onChange={(e) => setCell(i, "name", e.target.value)}
                      placeholder="Optional label"
                      className="w-full text-xs px-2 py-1 bg-background border border-border"
                      data-testid={`effort-name-${line.id}-${i}`} />
            </div>
            <div className="col-span-1">
              <input type="number" step="0.25" value={r.hours}
                      onChange={(e) => setCell(i, "hours", e.target.value)}
                      className="w-full text-xs px-2 py-1 bg-background border border-border tabular-nums text-right"
                      data-testid={`effort-hours-${line.id}-${i}`} />
            </div>
            {!ratesHidden && (
              <div className="col-span-2">
                <input type="number" step="1" value={r.hourly_rate}
                        onChange={(e) => setCell(i, "hourly_rate", e.target.value)}
                        className={`w-full text-xs px-2 py-1 bg-background border tabular-nums text-right ${isRateOutOfBand(r, designations) ? "border-rose-400 bg-rose-50" : "border-border"}`}
                        title={isRateOutOfBand(r, designations) ? "Rate outside designation band" : undefined}
                        data-testid={`effort-rate-${line.id}-${i}`} />
              </div>
            )}
            {!ratesHidden && (
              <div className="col-span-1 text-right font-mono text-xs tabular-nums">
                {fmt((Number(r.hours) || 0) * (Number(r.hourly_rate) || 0), currency)}
              </div>
            )}
            <div className="col-span-1 text-right">
              <button type="button" onClick={() => removeRow(i)}
                       className="text-muted-foreground hover:text-rose-500 p-0.5"
                       aria-label="Delete effort"
                       data-testid={`effort-remove-${line.id}-${i}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
            {/* R138ap · Cost-source detail strip (2nd row inside the same grid) */}
            {!ratesHidden && (
              <div className="col-span-12 flex flex-wrap items-center gap-2 pl-3 pt-1"
                   data-testid={`effort-cost-source-${line.id}-${i}`}>
                <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Cost from</span>
                <select value={r.cost_source || "designation"}
                        onChange={(e) => setCell(i, "cost_source", e.target.value)}
                        className="text-[10px] px-1 py-0.5 bg-background border border-border"
                        data-testid={`effort-cost-source-select-${line.id}-${i}`}>
                  <option value="designation">Designation rate</option>
                  <option value="custom">Custom rate</option>
                  <option value="user">Assigned user salary</option>
                  <option value="actual">Actual timesheet (soon)</option>
                </select>
                {r.cost_source === "custom" && (
                  <div className="flex items-center gap-1">
                    <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Cost/hr</span>
                    <input type="number" step="1" value={r.custom_internal_rate}
                            onChange={(e) => setCell(i, "custom_internal_rate", e.target.value)}
                            className="w-20 text-[10px] px-1 py-0.5 bg-background border border-border tabular-nums text-right"
                            data-testid={`effort-custom-rate-${line.id}-${i}`}
                            placeholder="0" />
                  </div>
                )}
                {r.cost_source === "user" && (
                  <select value={r.assigned_user_id || ""}
                          onChange={(e) => assignUser(i, e.target.value)}
                          className="text-[10px] px-1 py-0.5 bg-background border border-border max-w-[180px]"
                          data-testid={`effort-user-select-${line.id}-${i}`}>
                    <option value="">Pick user…</option>
                    {members.map((m) => (
                      <option key={m.user_id || m.id} value={m.user_id || m.id}>
                        {m.name || m.email}
                      </option>
                    ))}
                  </select>
                )}
                {(r.cost_source && r.cost_source !== "designation") && (
                  <span className="text-[9px] text-muted-foreground">
                    Cost used only in margin calc — does not change client-facing rate.
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
        {visibleRows.length > 0 && (
          <div className="grid grid-cols-12 gap-2 px-2 py-2 text-xs font-semibold border-t-2 border-border bg-muted/30 tabular-nums">
            <div className="col-span-7 text-right text-muted-foreground uppercase tracking-wide text-[10px]">Rollup</div>
            <div className="col-span-1 text-right" data-testid={`effort-rollup-hours-${line.id}`}>{totalHours.toFixed(2)}h</div>
            {!ratesHidden && <div className="col-span-2"></div>}
            {!ratesHidden && (
              <div className="col-span-1 text-right font-mono" data-testid={`effort-rollup-amount-${line.id}`}>
                {fmt(totalAmount, currency)}
              </div>
            )}
            <div className="col-span-1"></div>
          </div>
        )}
      </div>
      {/* R138bo · Side-by-side scenario cost/margin comparison so estimators
          can see all 4 cost-source outcomes at a glance without switching
          the picker on each row. */}
      {!ratesHidden && visibleRows.length > 0 && (
        <ScenarioCompareBar
          rows={visibleRows}
          designations={designations}
          members={members}
          currency={currency}
          revenue={totalAmount}
          lineId={line.id}
          wsId={wsId}
          estimateId={estimateId}
          currentRows={rows}
          onApplyScenario={(sourceKey) => {
            // R138bp · Bulk-flip every effort row in THIS line to the
            // chosen cost source. `designation` uses the designation's
            // default rate as the internal cost — we simply set
            // cost_source and leave the row's `hourly_rate` (client-
            // facing) alone since only the internal cost source changes.
            setRows((prev) => prev.map((r) => {
              // Skip rows that don't have the data needed for the source
              // (e.g. missing assigned_user_id for user source).
              if (sourceKey === "user" && !r.assigned_user_id) return r;
              if (sourceKey === "custom" && (!r.custom_internal_rate || Number(r.custom_internal_rate) === 0)) return r;
              return { ...r, cost_source: sourceKey, _dirty: true };
            }));
            setDirty(true);
            toast.success(`All rows switched to "${sourceKey.replace(/_/g, " ")}" cost basis — click Save efforts to persist.`);
          }}
        />
      )}
      {dirty && (
        <div className="text-[10px] text-amber-700">
          Unsaved effort changes — click <b>Save efforts</b> before switching to another line.
        </div>
      )}
    </div>
  );
}

// R138as · Effort Templates picker. Small dropdown that lists saved
// workspace templates and applies one to this line (append or replace).
function TemplatePicker({ wsId, lineId, onApplied }) {
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/effort-templates`);
      setTemplates(r.data?.rows || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line
  const apply = async (tid, mode) => {
    try {
      const r = await api.post(
        `/estimate-line-items/${lineId}/apply-effort-template?template_id=${tid}&mode=${mode}`);
      toast.success(`Applied ${r.data?.applied_rows || 0} rows (${mode})`);
      setOpen(false);
      onApplied?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Apply failed")); }
  };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)}
              className="text-[10px] px-2 py-1 border border-border hover:bg-muted text-muted-foreground flex items-center gap-1"
              data-testid={`effort-templates-btn-${lineId}`}>
        Templates
      </button>
      {open && (
        <div className="absolute z-10 top-full mt-1 right-0 w-72 bg-background border border-border shadow-lg text-xs"
             data-testid={`effort-templates-dropdown-${lineId}`}
             onMouseLeave={() => setOpen(false)}>
          <div className="px-2 py-1.5 label-eyebrow text-[9px] border-b border-border bg-muted/40">
            Effort templates
          </div>
          {loading ? (
            <div className="px-3 py-2 text-muted-foreground text-[11px]">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="px-3 py-2 text-muted-foreground text-[11px]">
              No templates yet. Save one from an existing effort breakdown.
            </div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {templates.map((t) => (
                <div key={t.id} className="px-2 py-1.5 border-b border-border/40 hover:bg-muted/20"
                     data-testid={`effort-template-row-${t.id}`}>
                  <div className="font-semibold text-[11px] truncate" title={t.name}>{t.name}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {(t.rows || []).length} rows · {(t.rows || []).reduce((s, r) => s + (Number(r.hours) || 0), 0)}h
                    {t.usage_count ? ` · used ${t.usage_count}×` : ""}
                  </div>
                  <div className="flex gap-1 mt-1">
                    <button type="button" onClick={() => apply(t.id, "append")}
                            className="text-[10px] px-1.5 py-0.5 border border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                            data-testid={`effort-template-append-${t.id}`}>
                      Append
                    </button>
                    <button type="button" onClick={() => apply(t.id, "replace")}
                            className="text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground hover:bg-muted"
                            data-testid={`effort-template-replace-${t.id}`}>
                      Replace all
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// R138bo · Scenario Compare Bar — shows the 4 cost-source outcomes
// side by side so estimators can pick the one that gives the best
// margin. Revenue (client-facing) is fixed across scenarios; only the
// INTERNAL cost changes, hence the margin varies.
//
// Sources:
//   1. Designation  — uses `designation.default_hourly_rate` per row.
//   2. Custom       — uses `row.custom_internal_rate` per row.
//   3. User salary  — hourly = employee.salary_amount / (22 × 8) for the
//                     row's `assigned_user_id`.
//   4. Actual       — uses `row.actual_hours × row.actual_effective_rate`
//                     when the sub-item has already been linked to a
//                     project + timesheet entries. Otherwise N/A.
function ScenarioCompareBar({ rows, designations, members, currency, revenue, lineId, wsId, estimateId, onApplyScenario }) {
  const desMap = useMemo(() => {
    const m = {};
    (designations || []).forEach((d) => { m[d.id] = d; });
    return m;
  }, [designations]);
  const userHourlyMap = useMemo(() => {
    const m = {};
    (members || []).forEach((mem) => {
      const uid = mem.user_id || mem.id;
      const sal = Number(mem.salary_amount || mem.salary || 0);
      if (uid && sal > 0) m[uid] = sal / (22 * 8);   // ~176 working hours per month
    });
    return m;
  }, [members]);

  // R138bu · "Current" cost — walk rows honouring the row's persisted
  // cost_source so the compare-delta arrow shows real savings/losses.
  const currentCost = useMemo(() => {
    let cur = 0;
    (rows || []).forEach((s) => {
      const h = Number(s.hours) || 0;
      const dRate = s.designation_id && desMap[s.designation_id]
        ? Number(desMap[s.designation_id].default_hourly_rate || 0)
        : Number(s.hourly_rate || 0);
      const cRate = Number(s.custom_internal_rate || 0);
      const uRate = s.assigned_user_id ? (userHourlyMap[s.assigned_user_id] || 0) : 0;
      const aH = Number(s.actual_hours || 0);
      const aR = Number(s.actual_effective_rate || 0);
      const src = (s.cost_source || "designation").toLowerCase();
      if (src === "custom" && cRate > 0) cur += h * cRate;
      else if (src === "user" && uRate > 0) cur += h * uRate;
      else if (src === "actual" && aH > 0 && aR > 0) cur += aH * aR;
      else if (dRate > 0) cur += h * dRate;
    });
    return cur;
  }, [rows, desMap, userHourlyMap]);


  const scenarios = useMemo(() => {
    let designationCost = 0;
    let customCost = 0;
    let userCost = 0;
    let actualCost = 0;
    let designationCoverage = 0;
    let customCoverage = 0;
    let userCoverage = 0;
    let actualCoverage = 0;
    const totalHours = rows.reduce((s, r) => s + (Number(r.hours) || 0), 0);

    rows.forEach((r) => {
      const h = Number(r.hours) || 0;
      // Designation rate
      const dRate = r.designation_id && desMap[r.designation_id]
        ? Number(desMap[r.designation_id].default_hourly_rate || 0)
        : Number(r.hourly_rate || 0);
      if (dRate > 0) { designationCost += h * dRate; designationCoverage += h; }
      // Custom
      const cRate = Number(r.custom_internal_rate || 0);
      if (cRate > 0) { customCost += h * cRate; customCoverage += h; }
      // User salary
      const uRate = r.assigned_user_id ? (userHourlyMap[r.assigned_user_id] || 0) : 0;
      if (uRate > 0) { userCost += h * uRate; userCoverage += h; }
      // Actual timesheet — only if the sub-item was already linked/logged
      const aHours = Number(r.actual_hours || 0);
      const aRate = Number(r.actual_effective_rate || 0);
      if (aHours > 0 && aRate > 0) { actualCost += aHours * aRate; actualCoverage += aHours; }
    });

    const mkScenario = (key, label, cost, coverage) => {
      const covered = totalHours > 0 && coverage >= totalHours * 0.95;
      const partial = coverage > 0 && !covered;
      const missing = coverage === 0;
      const margin = revenue - cost;
      const marginPct = revenue > 0 ? (margin / revenue) * 100 : 0;
      return { key, label, cost, margin, marginPct, covered, partial, missing };
    };

    return [
      mkScenario("designation", "Designation", designationCost, designationCoverage),
      mkScenario("custom",      "Custom",      customCost,      customCoverage),
      mkScenario("user",        "User salary", userCost,        userCoverage),
      mkScenario("actual",      "Actual",      actualCost,      actualCoverage),
    ];
  }, [rows, desMap, userHourlyMap, revenue]);

  const bestMarginKey = useMemo(() => {
    const validScenarios = scenarios.filter((s) => !s.missing);
    if (validScenarios.length === 0) return null;
    return validScenarios.reduce((best, s) => (s.marginPct > best.marginPct ? s : best), validScenarios[0]).key;
  }, [scenarios]);

  // R138bp · AI Margin Coach state — small on-demand explanation from
  // the workspace's Emergent LLM key. Cached until the row set changes.
  const [coachOpen, setCoachOpen] = useState(false);
  const [coachText, setCoachText] = useState("");
  const [coachLoading, setCoachLoading] = useState(false);
  const askCoach = async () => {
    if (coachText) { setCoachOpen((v) => !v); return; }
    setCoachOpen(true);
    setCoachLoading(true);
    try {
      // R138bu · Try the cache first — backend stores the last coach
      // reason per line keyed by winning scenario. Only POST (which
      // burns LLM tokens) when the cache is missing or stale.
      if (estimateId && lineId && bestMarginKey) {
        try {
          const { data: cached } = await api.get(
            `/workspaces/${wsId}/estimates/${estimateId}/scenario-coach-cache`,
            { params: { line_id: lineId, best_key: bestMarginKey } });
          if (cached?.cached && cached.reason) {
            setCoachText(`${cached.reason} · cached`);
            setCoachLoading(false);
            return;
          }
        } catch { /* cache miss is fine — fall through to POST */ }
      }
      const { data } = await api.post(
        `/workspaces/${wsId}/estimates/scenario-coach`,
        {
          revenue,
          scenarios: scenarios.map((s) => ({
            key: s.key, label: s.label, cost: s.cost,
            margin: s.margin, margin_pct: s.marginPct,
            missing: s.missing, partial: s.partial,
          })),
          best_key: bestMarginKey,
          row_count: rows.length,
          estimate_id: estimateId,
          line_id: lineId,
        });
      setCoachText(data?.reason || "The model didn't return an explanation.");
    } catch (e) {
      setCoachText(`Failed to generate: ${e?.response?.data?.detail?.message || e?.message || "unknown error"}`);
    } finally { setCoachLoading(false); }
  };
  // Reset AI cache when scenarios materially change
  useEffect(() => { setCoachText(""); setCoachOpen(false); }, [rows.length]);

  return (
    <div className="mt-2 border border-border bg-muted/10"
         data-testid={`scenario-compare-${lineId}`}>
      <div className="px-2 py-1.5 border-b border-border bg-muted/40 flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
          Cost-source scenario compare
        </span>
        <span className="text-[9px] text-muted-foreground">
          Revenue (fixed) · <span className="font-mono tabular-nums font-semibold text-foreground">{fmt(revenue, currency)}</span>
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4">
        {scenarios.map((s) => {
          const isBest = s.key === bestMarginKey && !s.missing;
          const tone = s.missing
            ? "bg-muted/20 opacity-60"
            : isBest
              ? "bg-emerald-50 border-l-2 border-emerald-500"
              : s.marginPct < 20
                ? "bg-rose-50"
                : "";
          // R138bp · Cells become click-to-apply CTAs when the scenario
          // is usable. Missing scenarios are read-only.
          const clickable = !s.missing && !!onApplyScenario;
          const Wrapper = clickable ? "button" : "div";
          return (
            <Wrapper key={s.key}
                 type={clickable ? "button" : undefined}
                 onClick={clickable ? () => onApplyScenario(s.key) : undefined}
                 title={clickable ? `Apply "${s.label}" cost basis to all rows in this line` : undefined}
                 className={`text-left w-full px-2 py-1.5 border-r last:border-r-0 border-border tabular-nums transition ${tone} ${clickable ? "hover:bg-primary/5 cursor-pointer" : ""}`}
                 data-testid={`scenario-${lineId}-${s.key}`}
                 data-margin-pct={s.marginPct.toFixed(2)}
                 data-is-best={isBest ? "true" : "false"}>
              <div className="flex items-baseline justify-between gap-1">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  {s.label}
                </span>
                {isBest && (
                  <span className="text-[8px] uppercase tracking-widest bg-emerald-600 text-white px-1 py-0.5">
                    Best
                  </span>
                )}
                {s.partial && (
                  <span className="text-[8px] uppercase tracking-widest text-amber-700"
                        title="Some rows don't have this cost source set">
                    Partial
                  </span>
                )}
              </div>
              {s.missing ? (
                <div className="text-[10px] text-muted-foreground italic mt-0.5">
                  {s.key === "actual" ? "Needs project link + timesheet" : "No data yet"}
                </div>
              ) : (
                <>
                  <div className="text-[10px] text-muted-foreground mt-0.5 flex justify-between">
                    <span>Cost</span>
                    <span className="font-mono">{fmt(s.cost, currency)}</span>
                  </div>
                  <div className="text-xs font-semibold flex justify-between mt-0.5">
                    <span>Margin</span>
                    <span className={`font-mono ${s.margin < 0 ? "text-rose-700" : s.marginPct < 20 ? "text-amber-700" : "text-emerald-700"}`}>
                      {fmt(s.margin, currency)}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground flex justify-between">
                    <span>Margin %</span>
                    <span className={`font-mono font-semibold ${s.marginPct < 0 ? "text-rose-700" : s.marginPct < 20 ? "text-amber-700" : "text-emerald-700"}`}>
                      {s.marginPct.toFixed(1)}%
                    </span>
                  </div>
                  {clickable && (
                    <div className="mt-1 text-[9px] uppercase tracking-widest text-primary/70">
                      Click to apply →
                    </div>
                  )}
                  {/* R138bu · Compare delta vs currently-selected sources */}
                  {!s.missing && Math.abs(currentCost - s.cost) > 0.5 && (() => {
                    const delta = currentCost - s.cost;
                    const dPct = revenue > 0 ? (delta / revenue) * 100 : 0;
                    return (
                      <div className={`flex items-baseline justify-between text-[10px] font-mono ${delta > 0 ? "text-emerald-700" : "text-rose-700"}`}
                            data-testid={`scenario-${lineId}-${s.key}-delta`}>
                        <span className="uppercase tracking-widest opacity-70">vs current</span>
                        <span className="font-bold">
                          {delta > 0 ? "▲ +" : "▼ "}{fmt(Math.abs(delta), currency)}
                          <span className="opacity-70 ml-1">({delta >= 0 ? "+" : ""}{dPct.toFixed(1)}%)</span>
                        </span>
                      </div>
                    );
                  })()}
                </>
              )}
            </Wrapper>
          );
        })}
      </div>
      {/* R138bp · AI Margin Coach — asks the LLM why the winning
          scenario is winning, so junior estimators learn the pattern. */}
      <div className="px-2 py-1.5 border-t border-border bg-muted/20 flex items-start gap-2">
        <button
          type="button"
          onClick={askCoach}
          disabled={coachLoading || !bestMarginKey}
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-50 shrink-0"
          data-testid={`scenario-coach-btn-${lineId}`}
          title="Ask AI to explain the margin story"
        >
          <Sparkles className="w-3 h-3" />
          {coachOpen ? (coachText ? "Hide coach" : "Thinking…") : "AI margin coach"}
        </button>
        {coachOpen && (
          <div className="text-[11px] leading-relaxed italic text-foreground/80 border-l-2 border-primary/50 pl-2"
               data-testid={`scenario-coach-text-${lineId}`}>
            {coachLoading ? "Analysing the numbers…" : (coachText || "No explanation yet.")}
          </div>
        )}
      </div>
    </div>
  );
}
